import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../hooks/useApi';
import { useSnackbar } from '../Stateless/SnackBarProvider';
import { useEffect } from 'react';
import { useQueryParams } from '../HelperFunctions/Hooks/useQueryParams';
import { useNavigate } from 'react-router-dom';

interface ApiError extends Error {
  response?: {
    data?: {
      message?: string;
    };
  };
}

// Add Store Category
export const useAddStoreQuery = () => {
  const { addStoreOwner } = useApi();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: addStoreOwner,
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['storeOwners'] });
      showMessage(
        response?.message || 'Store owner added successfully',
        'success'
      );
      navigate('/store-owner');
    },
    onError: (error: ApiError) => {
      console.error('Error adding store owner:', error);
      showMessage(
        error.response?.data?.message || 'Error adding store owner',
        'error'
      );
    },
  });
};

// List Store Category
export const useGetListCategories = ({
  isMainScreen = false,
  initialData,
}: {
  initialData?: any;
  isMainScreen?: boolean;
  key?: any;
}) => {
  const { query, queryhash, setFilters, filters, setQuery, updateQuery } =
    useQueryParams<any>({
      isMainScreen,
      initialData,
    });
  const { getListCategories } = useApi();
  const {
    data,
    isLoading: isFetching,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['categories', query.page, query.limit, query.sortDirection],
    queryFn: () => {
      return getListCategories(query);
    },
  });
  const isLoading = isFetching || isRefetching;
  useEffect(() => {
    if (queryhash) {
      refetch();
    }
  }, [queryhash]);

  return {
    data,
    isLoading,
    query,
    setQuery,
    filters,
    setFilters,
    updateQuery,
  };
};

//Delete Store Category
export const useDeleteStoreCategoryById = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { deleteStoreCategoryById } = useApi();

  return useMutation({
    mutationKey: ['DeleteStoreCategory'],
    mutationFn: (id: string) => deleteStoreCategoryById(id),
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'SubAdmin deleted successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['categories'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to deleted SubAdmin',
        'error'
      );
    },
  });
};

// Block and unblock Category
export const useBlockOrUnblockStoreCategoryById = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { blockOrUnblockStoreCategoryById } = useApi();

  return useMutation({
    mutationKey: ['BlockOrUnblockStoreCategory'],
    mutationFn: ({ id, data }: { id: string[]; data: any }) =>
      blockOrUnblockStoreCategoryById({ id, status: data.status }),
    onSuccess: () => {
      showMessage('Store Category blocked successfully', 'success');
      queryClient.invalidateQueries({
        queryKey: ['categories'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.detail?.[0]?.type ||
        'Failed to block the Store Category',
        'error'
      );
    },
  });
};

type Params = {
  sortBy?: string;
 sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  addedByFilter?: string;
  statusFilter?: string;
  search?: string;
};

// Store Owner API
export const useGetAllStoreOwnersQuery = (queryParams: Params) => {
  const { getAllStoreOwners } = useApi();

  const {data,isLoading: isFetching,refetch,isRefetching,} = useQuery({
    queryKey: ['storeOwners', queryParams],
    queryFn: () => {
      return getAllStoreOwners(queryParams);
    },
  });

  const isLoading = isFetching || isRefetching;
  return {
    data,
    isLoading,
    refetch,
  };
};

export const useDeleteStoreOwnerByIdQuery = () => {
  const { deleteStoreOwnerById } = useApi();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: deleteStoreOwnerById,
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['storeOwners'] });
      showMessage(
        response?.message || 'Store owner deleted successfully',
        'success'
      );
      navigate('/store-owner');
    },
    onError: (error: ApiError) => {
      console.error('Error deleting store owner:', error);
      showMessage(
        error.response?.data?.message || 'Error deleting store owner',
        'error'
      );
    },
  });
};

export const useUpdateStoreOwnerByIdQuery = () => {
  const { updateStoreOwner } = useApi();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (params: { id: string; data: any }) =>
      updateStoreOwner(params.id, params.data),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['storeOwners'] });
      showMessage(
        response?.message || 'Store owner updated successfully',
        'success'
      );
      navigate('/store-owner');
    },
    onError: (error: ApiError) => {
      console.error('Error updating store owner:', error);
      showMessage(
        error.response?.data?.message || 'Error updating store owner',
        'error'
      );
    },
  });
};

export const useToggleStoreOwnerStatusQuery = () => {
  const { toggleStoreOwnerStatus } = useApi();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (params: { id: string; status: string }) =>
      toggleStoreOwnerStatus(params.id, params.status),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['storeOwners'] });
      showMessage(
        response?.message || 'Store owner status updated successfully',
        'success'
      );
      navigate('/store-owner');
    },
    onError: (error: ApiError) => {
      console.error('Error updating store owner status:', error);
      showMessage(
        error.response?.data?.message || 'Error updating store owner status',
        'error'
      );
    },
  });
};
